import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  TextField, 
  Typography, 
  Paper, 
  Container, 
  Alert, 
  CircularProgress, 
  Stepper, 
  Step, 
  StepLabel,
  InputAdornment,
  IconButton
} from '@mui/material';
import { ArrowBack, Visibility, VisibilityOff } from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import { authService } from '../../services/auth';
import { styles } from './styles';
import { darkModeTextField } from '../../styles/textFieldStyles';

const ResetPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  const steps = ['Verificar código', 'Criar nova senha'];

  const toggleShowNewPassword = () => {
    setShowNewPassword(!showNewPassword);
  };

  const toggleShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      const isValid = await authService.verifyResetCode(email, resetCode);
      
      if (isValid) {
        setActiveStep(1);
      } else {
        setError('Código inválido ou expirado. Verifique e tente novamente.');
      }
    } catch (err: any) {
      console.error('Erro ao verificar código:', err);
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.error || 
                          'Ocorreu um erro ao verificar o código. Tente novamente mais tarde.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      setError('As senhas não coincidem');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      await authService.resetPassword(email, resetCode, newPassword, confirmPassword);
      setSuccess(true);
    } catch (err: any) {
      console.error('Erro ao redefinir senha:', err);
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.error || 
                          'Ocorreu um erro ao redefinir sua senha. Tente novamente mais tarde.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const renderVerifyCodeStep = () => (
    <Box component="form" onSubmit={handleVerifyCode} sx={styles.form}>
      <TextField
        variant="outlined"
        margin="normal"
        required
        fullWidth
        id="email"
        label="E-mail"
        name="email"
        autoComplete="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        disabled={loading}
        sx={darkModeTextField}
      />
      <TextField
        variant="outlined"
        margin="normal"
        required
        fullWidth
        name="resetCode"
        label="Código de redefinição"
        id="resetCode"
        autoComplete="off"
        value={resetCode}
        onChange={(e) => setResetCode(e.target.value)}
        disabled={loading}
        sx={darkModeTextField}
      />
      <Button
        type="submit"
        fullWidth
        variant="contained"
        color="primary"
        disabled={loading}
        sx={styles.button}
      >
        {loading ? <CircularProgress size={24} /> : 'Verificar código'}
      </Button>
    </Box>
  );

  const renderResetPasswordStep = () => (
    <Box component="form" onSubmit={handleResetPassword} sx={styles.form}>
      <TextField
        variant="outlined"
        margin="normal"
        required
        fullWidth
        name="newPassword"
        label="Nova senha"
        type={showNewPassword ? 'text' : 'password'}
        id="newPassword"
        autoComplete="new-password"
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
        disabled={loading}
        sx={darkModeTextField}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={toggleShowNewPassword}
                edge="end"
                sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
              >
                {showNewPassword ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <TextField
        variant="outlined"
        margin="normal"
        required
        fullWidth
        name="confirmPassword"
        label="Confirmar nova senha"
        type={showConfirmPassword ? 'text' : 'password'}
        id="confirmPassword"
        autoComplete="new-password"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        disabled={loading}
        error={newPassword !== confirmPassword && confirmPassword !== ''}
        helperText={newPassword !== confirmPassword && confirmPassword !== '' ? 'As senhas não coincidem' : ''}
        sx={darkModeTextField}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={toggleShowConfirmPassword}
                edge="end"
                sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
              >
                {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <Button
        type="submit"
        fullWidth
        variant="contained"
        color="primary"
        disabled={loading}
        sx={styles.button}
      >
        {loading ? <CircularProgress size={24} /> : 'Redefinir senha'}
      </Button>
    </Box>
  );

  return (
    <Container component="main" maxWidth="xs">
      <Paper elevation={3} sx={styles.paper}>
        <Box sx={styles.header}>
          <Typography component="h1" variant="h5">
            Redefinição de Senha
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={styles.subtitle}>
            {activeStep === 0 
              ? 'Informe seu e-mail e o código recebido' 
              : 'Crie uma nova senha segura'}
          </Typography>
        </Box>

        <Stepper activeStep={activeStep} sx={styles.stepper}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={styles.alert}>
            {error}
          </Alert>
        )}

        {success ? (
          <Box sx={styles.successMessage}>
            <Alert severity="success" sx={styles.alert}>
              Senha redefinida com sucesso! Você já pode fazer login com sua nova senha.
            </Alert>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={() => navigate('/login')}
              sx={styles.button}
            >
              Ir para o login
            </Button>
          </Box>
        ) : (
          <>
            {activeStep === 0 ? renderVerifyCodeStep() : renderResetPasswordStep()}
            
            <Button
              startIcon={<ArrowBack />}
              component={Link}
              to="/login"
              sx={styles.backButton}
            >
              Voltar para o login
            </Button>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default ResetPassword;